import './DesignerPalette.css';

import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tabs, Tab } from 'react-bootstrap';
import NodeContent from './NodeContent';
import { useSelector } from 'react-redux';
import { Designer } from '../../api/Designer';
import { applicationAccessLevel, selectedModeSelector } from '../../slices/designerSlice';
import { useToasts } from '../../contexts/toasts';
import { memo } from 'react';
import AppSearch from '../common/AppSearch';
import { isStreamService } from '../../utils/DesignerUtils';
import { TailSpin } from 'react-loader-spinner';
import { Alert, Tooltip } from 'antd';
import { useCallback } from 'react';
import { getNodePortsFromProperties } from '../../utils';
import { AccessLevel } from '../../models/generics';

type AlertProps = {
  type?: 'service' | 'dataset' | 'model';
  group?: 'services' | 'datasets' | 'models';
  selectedMode?: string;
}

const EmptyAlert = memo(({ type, selectedMode, group }: AlertProps) => (
  <Alert
    type="info"
    message={
      type === 'service'
        ? <span>No <b>{selectedMode}</b> services found.</span>
        : <span>No {group} found.</span>
    }
    showIcon
  />
))

const tabs = ["services", "datasets", "models"];

type NodeProps = {
  node: any;
  mode: string;
  onNodeDragStart: (evt: React.DragEvent<HTMLLIElement>, node: any, type: string) => void;
}

const ServiceNode = memo(({ node, mode, onNodeDragStart }: NodeProps) => {
  const handleDragStart = (event: React.DragEvent<HTMLLIElement>) => onNodeDragStart(event, node, 'service');

  return (
    <Tooltip
      title={
        <div className="text-center">
          <h6 className="mb-0">{node.name} {node.version}</h6>
          <p className="m-0">
            <FontAwesomeIcon icon={solid('user')} />
            <span className="ms-2">{node.owner}</span>
          </p>
        </div>
      }
      placement="right"
    >
      <li
        id={`service-${node.id}`}
        className={classNames(['designer-node', 'service-node', mode])}
        onDragStart={handleDragStart}
        // onDrag={(event) => onNodeDrag(event, node.id)}
        // onDragEnd={(event) => onNodeDragEnd(event, node.id)}
        draggable>
        <NodeContent title={node.name} version={node.version} type={mode} />
      </li>
    </Tooltip>
  );
});

type PaletteNode = {
  id: number;
  name: string;
  type: 'service' | 'dataset' | 'model';
  [key: string]: any;
}

function DesignerPalette() {
  console.log("[Palette] re-render");
  const [searchText, setSearchText] = useState('');

  const [services, setServices] = useState<PaletteNode[]>();
  const [datasets, setDatasets] = useState<PaletteNode[]>();
  const [models, setModels] = useState<PaletteNode[]>();

  const [servicesError, setServicesError] = useState(false);
  const [datasetsError, setDatasetsError] = useState(false);
  const [modelsError, setModelsError] = useState(false);

  const selectedMode = useSelector(selectedModeSelector);
  const selectedAccessLevel = useSelector(applicationAccessLevel);
  const { showToast } = useToasts();

  const data: Record<string, PaletteNode[] | undefined> = useMemo(() => ({ services, datasets, models }), [datasets, models, services]);

  const initPalette = useCallback(() => {
    const params = new URLSearchParams();
    const accessLevelFilter = selectedAccessLevel === AccessLevel.PRIVATE
      ? null
      : selectedAccessLevel === AccessLevel.TEAM
        ? "PUBLIC,TEAM"
        : selectedAccessLevel;
    if (accessLevelFilter) params.set("accessLevel", accessLevelFilter);
    params.set("designerMode", "true");

    Designer.getServices(params)
      .then(
        ({ data }) => {
          const nodes = data.collection.map(service => {
            const { inputs, outputs } = getNodePortsFromProperties(service.properties!);

            return {
              id: service.id,
              name: service.name,
              label: service.label || service.name,
              mode: service.mode,
              area: service.area,
              type: 'service',
              version: service.version,
              owner: service.ownerName,
              inputs,
              outputs,
            }
          });
          setServices(nodes as PaletteNode[]);
        },
        (err) => {
          setServicesError(true);
          showToast('services-error', `Could not get services.`, 'Services Error', 'danger');
        }
      );

    params.set('evaluation', `${false}`);
    params.set('created', `${true}`);

    Designer.getDatasets(params)
      .then(
        ({ data }) => {
          const nodes = data.collection.map(dataset => ({
            id: dataset.id,
            name: dataset.name,
            type: 'asset',
            datasetType: dataset.datasetType,
          }));
          setDatasets(nodes as PaletteNode[]);
        },
        (err) => {
          setDatasetsError(true);
          showToast('datasets-error', `Could not get datasets.`, 'Datasets Error', 'danger');
        }
      );

    Designer.getModels(params)
      .then(
        ({ data }) => {
          const models = data.collection;
          const nodes = models.map(model => ({
            id: model.id,
            name: model.name,
            type: 'asset',
            location: model.location,
            trailing: model.creationDateTime,
          }));
          setModels(nodes as PaletteNode[]);
        },
        (err) => {
          setModelsError(true);
          showToast('models-error', `Could not get models.`, 'Models Error', 'danger');
        }
      );
  }, [selectedAccessLevel, showToast]);

  useEffect(() => initPalette(), [selectedAccessLevel, initPalette]);

  const onNodeDragStart = useCallback((event: React.DragEvent<HTMLLIElement>, node: PaletteNode, group: string) => {
    // console.log('[START] node drag:', node);
    // const nullImage = document.createElement('div');
    // event.dataTransfer.setDragImage(nullImage, 0, 0);
    // event.dataTransfer.setData('application/reactflow', node.type);
    event.dataTransfer.setData('application/node-name', node.name);
    event.dataTransfer.setData('application/node-id', `${node.id}`);
    event.dataTransfer.setData('application/node-inputs', JSON.stringify(node.inputs));
    event.dataTransfer.setData('application/node-outputs', JSON.stringify(node.outputs));

    if (group === 'service') {
      const category = isStreamService(node) ? 'stream' : 'batch';
      event.dataTransfer.setData('application/node-label', node.label);
      event.dataTransfer.setData('application/node-category', category);
      event.dataTransfer.setData('application/node-version', node.version);
      event.dataTransfer.setData('application/node-owner', node.owner);
    }
    else
      event.dataTransfer.setData('application/node-category', group);

    if (group === "model") {
      event.dataTransfer.setData('application/node-model-location', node.location);
      event.dataTransfer.setData('application/node-trailing', node.trailing);
    }

    event.dataTransfer.setData('application/node-mode', node.mode);
    event.dataTransfer.effectAllowed = 'move';

    // const nodeId = 'node-drag-ghost-' + node.id;
    // const newElement = document.createElement('div');
    // newElement.id = nodeId;
    // newElement.classList.add('designer-node', 'node-drag-ghost');
    // if (node.type !== 'default') newElement.classList.add(node.type);
    // newElement.style.top = event.clientY + 'px';
    // newElement.style.left = event.clientX + 'px';
    // newElement.innerHTML = node.name;
    // document.body.appendChild(newElement);
  }, []);

  // const onNodeDrag = (event, id) => {
  //   const nodeId = 'node-drag-ghost-' + id;
  //   const element = document.getElementById(nodeId);
  //   element.style.top = event.clientY + 'px';
  //   element.style.left = event.clientX + 'px';
  // }

  // const onNodeDragEnd = (event, id) => {
  //   const nodeId = 'node-drag-ghost-' + id;
  //   const element = document.getElementById(nodeId);
  //   document.body.removeChild(element);
  // }

  const handleSearchChange = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    setSearchText(value);
  }, []);

  const renderServices = useCallback((nodes: PaletteNode[]) => {
    if (nodes!.length === 0) return <EmptyAlert group="services" />

    const selectedModeServices = nodes!.filter(node => selectedMode === 'BATCH' ? node.mode === selectedMode : isStreamService(node));
    if (selectedModeServices.length === 0) return <EmptyAlert type="service" selectedMode={selectedMode} />

    const grouped = selectedModeServices
      .reduce((accumulator, current) => {
        const group = selectedMode === 'BATCH' ? current.area : current.mode;
        if (accumulator[group]) accumulator[group].push(current);
        else accumulator[group] = [current];
        return accumulator;
      }, {} as Record<string, PaletteNode[]>);

    return (
      <div className="services-content">
        {Object.keys(grouped)
          .sort((a, b) => {
            if (selectedMode !== 'BATCH') {
              if (a === 'SOURCE' || b === 'SINK') return -1;
              if (a === 'PROCESSOR' && b === 'SOURCE') return 1;
              if (a === 'PROCESSOR' && b === 'SINK') return -1;
              if (a === 'SINK' && b === 'PROCESSOR') return 1;
              return 0;
            }
            return 0;
          })
          .map(group => (
            <div className="services-group" key={group}>
              <button className="stream-group-trigger" data-bs-toggle="collapse" data-bs-target={`#${group}-collapse`}>
                <span className="me-3">{group}</span>
                <FontAwesomeIcon className="ms-auto" icon={solid('chevron-down')} />
              </button>

              <div className="collapse show" id={`${group}-collapse`}>
                <ul className="stream-nodes">
                  {
                    grouped[group]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(node => {
                        const mode = node.mode.toLowerCase();
                        return <ServiceNode key={node.id} node={node} mode={mode} onNodeDragStart={onNodeDragStart} />;
                      })
                  }
                </ul>
              </div>
            </div>
          ))
        }
      </div>
    );
  }, [onNodeDragStart, selectedMode]);

  const renderDatasets = useCallback((nodes: PaletteNode[]) => {
    if (nodes!.length === 0) return <EmptyAlert group="datasets" />

    const grouped = nodes!
      .reduce((accumulator, current) => {
        const group = current.datasetType;
        if (accumulator[group]) accumulator[group].push(current);
        else accumulator[group] = [current];
        return accumulator;
      }, {} as Record<string, PaletteNode[]>);

    return (
      <div className="services-content">
        {Object.keys(grouped)
          .map(group => (
            <div className="services-group" key={group}>
              <button className="stream-group-trigger" data-bs-toggle="collapse" data-bs-target={`#${group}-collapse`}>
                <span className="me-3 text-uppercase">{group}</span>
                <FontAwesomeIcon className="ms-auto" icon={solid('chevron-down')} />
              </button>

              <div className="collapse show" id={`${group}-collapse`}>
                <ul className="stream-nodes">
                  {grouped[group]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(node => (
                      <Tooltip key={node.id} title={node.name} placement="right">
                        <li
                          id={`dataset-${node.id}`}
                          className={classNames(['designer-node', 'asset-node', "dataset"])}
                          onDragStart={(event) => onNodeDragStart(event, node, "dataset")}
                          // onDrag={(event) => onNodeDrag(event, node.id)}
                          // onDragEnd={(event) => onNodeDragEnd(event, node.id)}
                          draggable>
                          <NodeContent title={node.name} type="dataset" trailing={node.trailing} />
                        </li>
                      </Tooltip>
                    ))}
                </ul>
              </div>
            </div>
          ))
        }
      </div>
    );
  }, [onNodeDragStart]);

  const renderModels = useCallback((nodes: PaletteNode[]) => {
    if (nodes!.length === 0) return <EmptyAlert group="models" />

    return nodes!.map(node => (
      <Tooltip key={node.id} title={node.name} placement="right">
        <li
          id={`model-${node.id}`}
          className={classNames(['designer-node', 'asset-node', "model"])}
          onDragStart={(event) => onNodeDragStart(event, node, "model")}
          // onDrag={(event) => onNodeDrag(event, node.id)}
          // onDragEnd={(event) => onNodeDragEnd(event, node.id)}
          draggable>
          <NodeContent title={node.name} type="model" trailing={node.trailing} />
        </li>
      </Tooltip>
    ));
  }, [onNodeDragStart]);

  const renderAsyncContent = useCallback((group: string) => {
    if (
      (servicesError && group === "services") ||
      (datasetsError && group === "datasets") ||
      (modelsError && group === "models")
    ) return (
      <Alert
        showIcon
        type="error"
        message={`An error occured while retrieving ${group}, try again later.`}
        className="m-2"
      />
    );

    if (data[group]) {
      const filtered = data[group]!
        .filter(node => node.name.toLowerCase().includes(searchText.toLowerCase()));

      return (
        <>
          <div className="p-2 border-bottom">
            <AppSearch className="m-0" placeholder="Search elements..." value={searchText} onChange={handleSearchChange} />
          </div>

          <div className="palette-nodes">
            {group === 'services' && renderServices(filtered)}
            {group === 'datasets' && renderDatasets(filtered)}
            {group === 'models' && renderModels(filtered)}
          </div>
        </>
      )
    }
    else return (
      <div className="d-flex py-5 align-items-center justify-content-center">
        <TailSpin
          height={80}
          width={80}
          color="#677ab1"
          radius="1"
        />
      </div>
    )
  }, [data, datasetsError, modelsError, servicesError, renderServices, renderDatasets, renderModels, searchText, handleSearchChange]);

  return (
    <aside className="designer-palette">
      <Tabs id="palette-tabs" justify>
        {tabs.map(tab => (
          <Tab key={tab} eventKey={tab} title={tab}>{renderAsyncContent(tab)}</Tab>
        ))}
      </Tabs>
    </aside>
  );
}

export default memo(DesignerPalette);